import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  AtendimentoMensagem,
  AtendimentoRef,
  CentralAtendimentosData,
  CentralAtendimentosFiltros,
  DetalheAtendimento,
  PaginaAtendimentos,
  ResumoCasoNegociacao,
} from '../types';
import { selectCasoIdDaNegociacao } from './selectors';
import {
  buscarDetalheAtendimento,
  buscarHistoricoAtendimento,
  buscarResumoDoCaso,
  carregarPaginaAtendimentos,
  definirResponsavelAtendimento as definirResponsavelNoServidor,
  fetchCentralAtendimentosData,
  marcarAtendimentoLido,
} from './services';

export const fetchCentralAtendimentos = createAsyncThunk<
  CentralAtendimentosData & { currentUserId: string | null },
  CentralAtendimentosFiltros,
  { state: RootState; rejectValue: string }
>('centralAtendimentos/fetch', async (filtros, thunkAPI) => {
  const currentUserId = thunkAPI.getState().auth.user?.id ?? null;
  try {
    const dados = await fetchCentralAtendimentosData(filtros, currentUserId);
    return { ...dados, currentUserId };
  } catch {
    return thunkAPI.rejectWithValue('Não foi possível carregar a central de atendimentos.');
  }
});

export const carregarMaisAtendimentos = createAsyncThunk<
  PaginaAtendimentos,
  void,
  { state: RootState; rejectValue: string }
>('centralAtendimentos/carregarMais', async (_arg, thunkAPI) => {
  const { filtros, paginacao, latestListRequestId } = thunkAPI.getState().centralAtendimentos;
  const tab = filtros.tipoAtivo === 'notificacoes' ? 'notificacoes' : 'negociacoes';
  const cursor = paginacao[tab].nextCursor;

  if (!cursor) {
    return thunkAPI.rejectWithValue('Não há mais atendimentos para carregar.');
  }

  // O cursor em estado pertence à página que a lista atual terminou. Com um fetch de filtro em
  // voo, ele está a ponto de ser substituído — anexar a página seguinte dele misturaria filtros.
  if (latestListRequestId) {
    return thunkAPI.rejectWithValue('A lista está sendo recarregada.');
  }

  try {
    return await carregarPaginaAtendimentos(
      tab,
      filtros,
      thunkAPI.getState().auth.user?.id ?? null,
      cursor,
    );
  } catch {
    return thunkAPI.rejectWithValue('Não foi possível carregar mais atendimentos.');
  }
});

export const fetchHistoricoAtendimento = createAsyncThunk<
  AtendimentoRef & { mensagens: AtendimentoMensagem[] },
  AtendimentoRef,
  { rejectValue: string }
>('centralAtendimentos/fetchHistorico', async (ref, thunkAPI) => {
  try {
    return { ...ref, mensagens: await buscarHistoricoAtendimento(ref.id) };
  } catch {
    return thunkAPI.rejectWithValue('Não foi possível carregar as mensagens do atendimento.');
  }
});

export const fetchDetalheAtendimento = createAsyncThunk<
  AtendimentoRef & DetalheAtendimento,
  AtendimentoRef,
  { rejectValue: string }
>('centralAtendimentos/fetchDetalhe', async (ref, thunkAPI) => {
  try {
    return { ...ref, ...(await buscarDetalheAtendimento(ref.id)) };
  } catch {
    return thunkAPI.rejectWithValue('Não foi possível carregar os dados do atendimento.');
  }
});

/**
 * Revalidação de fundo da negociação aberta (evento em tempo real, polling, janela expirando):
 * relê só o caso. Falhar aqui rejeita em vez de cair num valor padrão, para uma oscilação do core
 * não zerar valor e situações do painel a cada ciclo — só a oferta, por cautela, sai de cena.
 */
export const revalidarCasoDaNegociacao = createAsyncThunk<
  { id: string } & ResumoCasoNegociacao,
  string,
  { state: RootState; rejectValue: string }
>(
  'centralAtendimentos/revalidarCaso',
  async (conversaId, thunkAPI) => {
    const casoId = selectCasoIdDaNegociacao(thunkAPI.getState(), conversaId);
    if (!casoId) return thunkAPI.rejectWithValue('Negociação sem caso vinculado.');
    try {
      return { id: conversaId, ...(await buscarResumoDoCaso(casoId)) };
    } catch {
      return thunkAPI.rejectWithValue('Não foi possível atualizar a negociação.');
    }
  },
  {
    condition: (conversaId, { getState }) =>
      selectCasoIdDaNegociacao(getState(), conversaId) !== null,
  },
);

export const definirResponsavelAtendimento = createAsyncThunk<
  AtendimentoRef & { responsavelId: string | null },
  AtendimentoRef & { responsavelId: string | null },
  { rejectValue: string }
>('centralAtendimentos/definirResponsavel', async (ref, thunkAPI) => {
  try {
    await definirResponsavelNoServidor(ref.id, ref.responsavelId);
    return ref;
  } catch {
    return thunkAPI.rejectWithValue('Não foi possível definir o responsável.');
  }
});

export const marcarAtendimentoComoLido = createAsyncThunk<void, string, { rejectValue: string }>(
  'centralAtendimentos/marcarLido',
  async (id, thunkAPI) => {
    try {
      await marcarAtendimentoLido(id);
    } catch {
      return thunkAPI.rejectWithValue('Não foi possível marcar o atendimento como lido.');
    }
  },
);
